import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

const getWebSocketUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // Convert http(s) to ws(s) and add /api prefix for Kubernetes routing
  const wsUrl = backendUrl.replace(/^http/, 'ws') + '/api';
  return wsUrl;
};

export const WebSocketProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  // iter163h — DM event bus. DMContext subscribes here so DM messages can flow
  // over the SAME WebSocket connection without needing a parallel socket.
  const messageListenersRef = useRef(new Set());
  const addMessageListener = useCallback((cb) => {
    messageListenersRef.current.add(cb);
    return () => messageListenersRef.current.delete(cb);
  }, []);

  const connect = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = `${getWebSocketUrl()}/ws/${token}`;
    console.log('Connecting to WebSocket:', wsUrl);

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 25000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          // Handle pong messages
          if (event.data === 'pong' || event.data === 'ping') {
            return;
          }

          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);

          // iter163h — broadcast every parsed payload to DM listeners so
          // DMContext can react to dm:* events without owning its own socket.
          messageListenersRef.current.forEach((cb) => {
            try { cb(data); } catch (e) { console.error('WS listener error:', e); }
          });

          if (data.type === 'connected') {
            console.log('WebSocket connection confirmed');
          } else if (data.type === 'notification') {
            // New notification received
            const notification = data.data;
            
            // Add to notifications list
            setNotifications(prev => [notification, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Determine notification type for styling
            const isMention = notification.template_key?.includes('mention');
            const isReply = notification.template_key?.includes('reply') || notification.template_key?.includes('thread');
            
            // Build navigation URL
            let viewUrl = `/requests/${notification.request_id}`;
            if (notification.comment_id) {
              viewUrl += `?tab=discussion&comment=${notification.comment_id}`;
              if (notification.template_key === 'doc_review_nudge') viewUrl += '&highlight=nudge';
            }
            
            // Show toast notification with appropriate styling
            const toastFn = isMention ? toast.warning : toast.info;
            toastFn(notification.message, {
              description: notification.preview ? `"${notification.preview.substring(0, 60)}..."` : 'New notification',
              action: notification.request_id ? {
                label: 'View',
                onClick: () => window.location.href = viewUrl
              } : undefined
            });
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Attempt to reconnect after 5 seconds if not a normal close
        if (event.code !== 1000 && token) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect WebSocket...');
            connect();
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [token]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logged out');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const markNotificationRead = useCallback((notificationId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_read',
        notification_id: notificationId
      }));
    }
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Connect when token is available
  useEffect(() => {
    if (token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  return (
    <WebSocketContext.Provider value={{
      isConnected,
      notifications,
      unreadCount,
      setUnreadCount,
      markNotificationRead,
      clearNotifications,
      addMessageListener,
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
